/**
 * A row of sibling views, one of them on screen — the plan's Przegląd · Kalendarz · Programowanie.
 *
 * Controls rather than a tab list: the views are not all panels of one screen (the builder is another
 * route), and a `tablist` would promise one panel swapping for another behind an arrow key.
 *
 * The view on screen is a BUTTON that carries `aria-current="page"` and does nothing when pressed. A
 * label on the current view and a button on the others made the pressed control a different element
 * after the navigation, so focus fell to `<body>` on every switch. A view with nowhere to go
 * (`onSelect: null`) is drawn the same as the others and is a plain label.
 */

import styles from './ViewSwitch.module.css';

export interface ViewSwitchItem {
  /** Stable across renders, so the pressed control survives the navigation it causes. */
  readonly key: string;
  readonly label: string;
  readonly current: boolean;
  /** `null` when the view cannot be opened from here. Ignored on the current view. */
  readonly onSelect: (() => void) | null;
}

export function ViewSwitch({ items }: { readonly items: readonly ViewSwitchItem[] }) {
  return (
    <div className={styles.switch}>
      {items.map((item) =>
        item.current ? (
          <button key={item.key} type="button" className={styles.on} aria-current="page">
            {item.label}
          </button>
        ) : item.onSelect === null ? (
          <span key={item.key} className={styles.off}>
            {item.label}
          </span>
        ) : (
          <button key={item.key} type="button" className={styles.off} onClick={item.onSelect}>
            {item.label}
          </button>
        ),
      )}
    </div>
  );
}
